import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { api } from '@/lib/api'
import { useConfirm } from '@/components/ui/ConfirmDialog'
import { useSearchTerm } from '@/hooks/use-search'
import { SearchInput } from '@/components/ui/SearchInput'

interface ChatSummary { id: string; title: string; updatedAt: string }

export function AssistantHistory({ activeChatId, disabled, onOpen, onDeleted }: {
  activeChatId: string | undefined
  /** Počas streamu / načítania chatu sa iný chat otvoriť nedá. */
  disabled?: boolean
  onOpen: (id: string) => void
  onDeleted: (id: string) => void
}) {
  const queryClient = useQueryClient()
  const confirm = useConfirm()
  const [search, setSearch] = useState('')
  const debounced = useSearchTerm(search)
  const { data: chats = [], isLoading } = useQuery({
    queryKey: ['assistant-chats', debounced],
    queryFn: async () => (await api.get<ChatSummary[]>('/assistant/chats', { params: debounced ? { search: debounced } : {} })).data,
  })
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/assistant/chats/${id}`),
    onSuccess: (_r, id) => {
      void queryClient.invalidateQueries({ queryKey: ['assistant-chats'] })
      onDeleted(id)
    },
  })

  const confirmDelete = async (chat: ChatSummary) => {
    const ok = await confirm({ title: 'Odstrániť konverzáciu', message: `„${chat.title}“`, confirmText: 'Odstrániť', danger: true })
    if (ok) remove.mutate(chat.id)
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="p-3">
        <SearchInput type="text" placeholder="Hľadať v konverzáciách…" value={search} onChange={setSearch} />
      </div>
      <ul className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {isLoading && <li className="px-2 py-3 text-sm text-gray-500 dark:text-gray-400">Načítavam…</li>}
        {!isLoading && chats.length === 0 && <li className="px-2 py-3 text-sm text-gray-500 dark:text-gray-400">Žiadne konverzácie.</li>}
        {chats.map((c) => (
          <li key={c.id} className="group flex items-center gap-1">
            <button
              type="button"
              disabled={disabled}
              onClick={() => onOpen(c.id)}
              className={`min-w-0 flex-1 rounded-lg px-2 py-2 text-left text-sm hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-700 ${c.id === activeChatId ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
            >
              <span className="block truncate text-gray-900 dark:text-gray-100">{c.title}</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                {new Date(c.updatedAt).toLocaleString('sk-SK', { timeZone: 'Europe/Bratislava', dateStyle: 'short', timeStyle: 'short' })}
              </span>
            </button>
            <button
              type="button"
              aria-label="Zmazať konverzáciu"
              title="Zmazať konverzáciu"
              disabled={remove.isPending}
              onClick={() => void confirmDelete(c)}
              className="rounded p-1.5 text-gray-400 hover:text-red-600 disabled:pointer-events-none [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:focus:opacity-100 [@media(hover:hover)]:group-hover:opacity-100"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
